// src/oura/components/search/MonthStory.tsx
//
// "What changed this month": the two lines at the top of Search performance
// that say what moved, before any tile or table. Written from the figures
// already on the screen, so it can never disagree with them.
//
// Facts only (Jack, 21 Oct 2026): it reports what the data shows and never
// what we will do. Oura cannot know what staff are planning, so "we're fixing
// the page" would be a promise nobody made.

import type { ReactNode } from "react";
import { SparklesIcon } from "lucide-react";
import type { AeoSearchPerformance } from "@service/aeo";
import { Surface, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { isCloseToPageOne, num } from "./format";

/** "up 16%", or "steady" under 1% either way. Whole percentages only. */
function change(percent: number): string {
    if (Math.abs(percent) < 1) return "steady";
    return `${percent > 0 ? "up" : "down"} ${Math.round(Math.abs(percent))}%`;
}

export function MonthStory({
    search,
    visits,
    visitsDeltaPct,
    note,
}: {
    search: AeoSearchPerformance;
    /** Sessions from Analytics; absent when it is not connected. */
    visits?: number | null;
    /** A percent (12.5 is 12.5%), unlike Search Console's fraction. */
    visitsDeltaPct?: number | null;
    /** Rendered under the lines, e.g. the past-scan warning. */
    note?: ReactNode;
}) {
    const totals = search.totals;
    const keywords = search.keywords ?? [];
    const clicksDelta = totals?.clicksDeltaPct;
    const hasVisitsDelta = visitsDeltaPct !== null && visitsDeltaPct !== undefined;

    // Each source on its own: with no earlier window it gives its total,
    // never a change we did not measure.
    const headline = [
        clicksDelta === null || clicksDelta === undefined
            ? `${num(totals?.clicks)} Google clicks`
            : `Google clicks ${change(clicksDelta * 100)}`,
        hasVisitsDelta
            ? `Visits ${change(visitsDeltaPct)}`
            : visits !== null && visits !== undefined ? `${num(visits)} visits` : null,
    ].filter((part): part is string => part !== null).join(". ") + ".";

    const moved = keywords.filter((k) => k.positionDelta !== null && k.positionDelta !== 0);
    const up = moved.filter((k) => k.positionDelta! < 0).length;
    const down = moved.length - up;
    const close = keywords.filter(isCloseToPageOne).length;
    // Most negative delta is the largest climb.
    const climber = moved.filter((k) => k.positionDelta! < 0).sort((a, b) => a.positionDelta! - b.positionDelta!)[0];
    const template = close > 0
        ? `${close} ${close === 1 ? "search is" : "searches are"} close to page 1 on Google.`
        : climber
            ? `Biggest climb: ${climber.query}, now at position ${climber.position.toFixed(1)}.`
            : null;

    // The backend's AI copy when one passed its checks (ourteam-backend#6426),
    // else the template above. The two never mix: a line from one beside a
    // line from the other could describe different figures.
    const saved = search.monthSummary;
    const first = saved ? saved.line1 : headline;
    const second = saved ? saved.line2 : template;
    const writeUp = saved?.writeUp;

    return (
        <Surface className="gap-3">
            <div className="flex items-center justify-between gap-3">
                <h2 className={cn(TYPE.tc, "m-0 font-semibold text-blue-600")}>What changed this month</h2>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-[12px] font-semibold text-blue-700">
                    <SparklesIcon aria-hidden size={13} />
                    Written by Oura
                </span>
            </div>
            <p className="m-0 text-[22px] font-semibold leading-[30px] tracking-[-0.02em] text-gray-900">{first}</p>
            {second && <p className={cn(TYPE.tb, "m-0 -mt-1 text-gray-600")}>{second}</p>}
            {/* Movement needs an earlier window; a first month has none, and
                "0 moved up" would claim a measurement we did not make. */}
            {(moved.length > 0 || close > 0) && (
                <div className="flex flex-wrap gap-2">
                    {moved.length > 0 && (
                        <>
                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-[13px] font-medium text-emerald-700">{up} moved up</span>
                            <span className="rounded-full bg-amber-50 px-3 py-1 text-[13px] font-medium text-amber-700">{down} slipped</span>
                        </>
                    )}
                    {close > 0 && <span className="rounded-full bg-blue-50 px-3 py-1 text-[13px] font-medium text-blue-700">{close} close to page 1</span>}
                </div>
            )}
            {writeUp && (
                <dl className="m-0 grid gap-x-4 gap-y-1.5 border-t border-gray-100 pt-3 sm:grid-cols-[max-content_1fr]">
                    {([["What happened", writeUp.happened], ["Why", writeUp.why], ["Worth knowing", writeUp.worthKnowing]] as const).map(([label, text]) => (
                        <div key={label} className="contents">
                            <dt className={cn(TYPE.tc, "font-semibold text-gray-500")}>{label}</dt>
                            <dd className={cn(TYPE.tb, "m-0 text-gray-900")}>{text}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {note}
        </Surface>
    );
}
